import React, { useRef, useEffect } from 'react'; // Added useEffect
import { Line, Bar } from 'react-chartjs-2';
// Chart object is implicitly used via react-chartjs-2 and global registration
// import Chart from 'chart.js/auto';
import { Typography, Box, Card, CardHeader, CardContent, Grid, Button, IconButton } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DownloadIcon from '@mui/icons-material/Download'; // Changed from ShareIcon
import { formatDrawName } from '../utils/drawUtils';
import { handleChartJsDownload, registerChartJsWatermarkGlobally } from '../utils/chartJsUtils'; // Import utilities

// --- Watermark Plugin and Download Handler are now in chartJsUtils.js ---

const RecentInvitations = ({ drawData }) => {
    // Register Chart.js plugin globally on component mount
    useEffect(() => {
        registerChartJsWatermarkGlobally();
    }, []); // Empty dependency array ensures this runs only once

    const lineChartRef = useRef(null);
    const barChartRef = useRef(null);
    const distributionChartRef = useRef(null);

    // --- Return early if no data ---
    if (!drawData) {
        return null;
    }

    if (drawData.length === 0) {
        return <Typography sx={{ mt: 2 }}>No draw data matches the current filter.</Typography>;
    }

    // --- Data Processing ---
    const sortedData = [...drawData].sort((a, b) => new Date(a.drawDate) - new Date(b.drawDate));

    // Determine a representative draw name for titles (use the first draw's name in the sorted list)
    // Assumes the drawData prop is already filtered for a specific type if needed
    const representativeDrawName = formatDrawName(sortedData[0].drawName); // Get formatted name from the earliest draw in the filtered set

    const displayDrawName =
        representativeDrawName === "No Program Specified"
            ? "All Draws"
            : representativeDrawName;

    const crsScoreRanges = [
        "601-1200", "501-600", "451-500", "491-500", "481-490",
        "471-480", "461-470", "451-460", "401-450", "441-450",
        "431-440", "421-430", "411-420", "401-410", "351-400",
        "301-350", "0-300"
    ];

    const latestDraw = sortedData[sortedData.length - 1]; // Already checked drawData.length > 0
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const previousYear = currentYear - 1;
    const currentMonthName = now.toLocaleString('default', { month: 'long' });

    const drawsThisMonth = sortedData.filter(d => {
        if (!d.drawDate) return false;
        try {
            const drawDt = new Date(d.drawDate);
            return drawDt.getMonth() === currentMonth && drawDt.getFullYear() === currentYear;
        } catch (e) { console.error("Error parsing date:", d.drawDate, e); return false; }
    }).length;

    const drawsThisYear = sortedData.filter(d => {
        if (!d.drawDate) return false;
        try {
            const drawDt = new Date(d.drawDate);
            return drawDt.getFullYear() === currentYear;
        } catch (e) { console.error("Error parsing date:", d.drawDate, e); return false; }
    }).length;

    let invitationsThisYear = 0;
    let invitationsPreviousYear = 0;

    sortedData.forEach(d => {
        if (!d.drawDate || !d.drawSize) return;
        try {
            const drawDt = new Date(d.drawDate);
            const drawYear = drawDt.getFullYear();
            const size = parseInt(d.drawSize.replace(/,/g, ''), 10);
            if (!isNaN(size)) {
                if (drawYear === currentYear) invitationsThisYear += size;
                else if (drawYear === previousYear) invitationsPreviousYear += size;
            }
        } catch (e) { console.error("Error processing draw for invitation sum:", d, e); }
    });

    // Filter data specifically for the current year's charts
    const chartData = sortedData.filter(d => {
        if (!d.drawDate) return false;
        try {
            return new Date(d.drawDate).getFullYear() === currentYear;
        } catch (e) { console.error("Error filtering date for chart:", d.drawDate, e); return false; }
    });

    const chartLabels = chartData.map((round) => round.drawDateFull);
    const chartCrsScores = chartData.map((round) => parseInt(round.drawCRS, 10));
    // We still need individual draw names for tooltips, even if title uses a representative one
    const chartDrawNames = chartData.map((round) => formatDrawName(round.drawName));
    const chartDrawSizes = chartData.map((round) => parseInt(round.drawSize?.replace(/,/g, ''), 10) || 0);

    // --- CRS Distribution Data ---
    let distributionChartData = null;
    let distributionDate = 'N/A';

    if (latestDraw && latestDraw.drawDistributionAsOn) {
        distributionDate = latestDraw.drawDistributionAsOn;
        const distributionCounts = crsScoreRanges.map((range, index) => {
            const key = `dd${index + 1}`;
            if (key === 'dd18') return null; // Exclude total if present as separate key
            const value = latestDraw[key];
            return value ? parseInt(value.replace(/,/g, ''), 10) : 0;
        }).filter(count => count !== null); // Remove null placeholder if dd18 was excluded

        const distributionLabels = crsScoreRanges.slice(0, distributionCounts.length); // Adjust labels to match count length

        if (distributionCounts.length === distributionLabels.length && distributionCounts.length > 0) {
            distributionChartData = {
                labels: distributionLabels,
                datasets: [{ label: `Number of Candidates in Pool`, data: distributionCounts, backgroundColor: 'rgba(255, 159, 64, 0.6)' }]
            };
        } else { console.error("Mismatch between distribution labels and counts after processing or no distribution data points found."); }
    }

    // --- Chart Data Structures ---
    const lineChartData = {
        labels: chartLabels,
        datasets: [{ label: `CRS Score Threshold (${displayDrawName} - ${currentYear})`, data: chartCrsScores, fill: false, borderColor: 'rgba(75,192,192,1)', tension: 0.1 }]
    };

    const barChartData = {
        labels: chartLabels,
        datasets: [{ label: `Number of Invitations (${displayDrawName} - ${currentYear})`, data: chartDrawSizes, backgroundColor: 'rgba(153,102,255,0.6)' }]
    };

    // --- Event Handlers ---
    const handleScrollTo = (targetId) => {
        const element = document.getElementById(targetId);
        if (element) {
            const headerOffset = 80; // Adjust as needed for your fixed header height
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        } else {
            console.warn(`Scroll target element with ID "${targetId}" not found.`);
        }
    };

    // handleShareClick is replaced by handleChartJsDownload from utils

    const scrollMarginTopValue = '80px'; // For CSS scroll-margin-top

    return (
        <Box sx={{ padding: { xs: 0, sm: 2 } }}>
            {/* --- Summary Card --- */}
            <Card className="myCard" sx={{ mb: 3 }}>
                {/* Use representative name in Summary Title too? Optional */}
                <CardHeader title={`${displayDrawName} - Draw Summary`} />
                <CardContent>
                    <Grid container spacing={2}>
                        {/* Latest draw details still specific to that draw */}
                        {latestDraw && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle1">Latest Draw ({latestDraw.drawDateFull || 'N/A'}):</Typography>
                            <Typography variant="body2" sx={{ pl: 2 }}>- Type: {formatDrawName(latestDraw.drawName)}</Typography> {/* Still show specific latest type */}
                            <Typography variant="body2" sx={{ pl: 2 }}>- CRS Score: {latestDraw.drawCRS || 'N/A'}</Typography>
                            <Typography variant="body2" sx={{ pl: 2 }}>- Invitations Issued: {latestDraw.drawSize || 'N/A'}</Typography>
                        </Grid>
                        )}
                        {/* Stats are based on the filtered drawData */}
                        <Grid item xs={12} sm={6}><Typography variant="body1">Draws in {currentMonthName} {currentYear}: <strong>{drawsThisMonth}</strong></Typography></Grid>
                        <Grid item xs={12} sm={6}><Typography variant="body1">Draws in {currentYear}: <strong>{drawsThisYear}</strong></Typography></Grid>
                        <Grid item xs={12} sm={6}><Typography variant="body1">Invitations in {currentYear}: <strong>{invitationsThisYear.toLocaleString()}</strong></Typography></Grid>
                        <Grid item xs={12} sm={6}><Typography variant="body1">Invitations in {previousYear}: <strong>{invitationsPreviousYear.toLocaleString()}</strong></Typography></Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pt: 1, borderTop: '1px solid var(--color-border)' }}>
                        <Button color="primary" variant="outlined" size="small" onClick={() => handleScrollTo('charts-section')} startIcon={<ArrowDownwardIcon />}>
                            View CRS Trend Chart
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* --- CRS Trend Chart Card --- */}
            <Card id="charts-section" className="myCard" sx={{ mb: 3, scrollMarginTop: scrollMarginTopValue }}>
                <CardHeader
                    // UPDATED TITLE
                    title={`Trend of CRS Scores for ${displayDrawName} (${currentYear})`}
                    action={
                        <IconButton aria-label="download crs trend chart" onClick={() => handleChartJsDownload(lineChartRef, `CRS Trend`, displayDrawName)}>
                            <DownloadIcon />
                        </IconButton>
                    }
                />
                <CardContent>
                    <Box sx={{ position: 'relative', height: '400px' }}>
                        <Line
                            ref={lineChartRef}
                            data={lineChartData} // Uses updated lineChartData with name in label
                            options={{
                                responsive: true, maintainAspectRatio: false,
                                plugins: {
                                    // watermarkPlugin, // Removed: Plugin is now registered globally
                                    tooltip: {
                                        mode: 'index', intersect: false, callbacks: {
                                            label: function (context) {
                                                let label = context.dataset.label || ''; // This now includes the representative name
                                                if (label) label += ': ';
                                                if (context.parsed.y !== null) {
                                                    // Tooltip still shows specific draw name for that point
                                                    const specificDrawName = chartDrawNames[context.dataIndex];
                                                    label += `${context.parsed.y} (${specificDrawName})`;
                                                }
                                                return label;
                                            }
                                        }
                                    },
                                    legend: { position: 'top' },
                                },
                                scales: { x: { display: true, title: { display: true, text: 'Draw Date' } }, y: { display: true, title: { display: true, text: 'CRS Score' } } }
                            }}
                            // Note: Plugin is applied via options.plugins above
                            // plugins={[watermarkPlugin]} // Removed redundant prop application
                        />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, pt: 1, borderTop: '1px solid var(--color-border)' }}>
                        <Button color="primary" variant="outlined" size="small" onClick={() => handleScrollTo('draw-size-chart-card')} startIcon={<ArrowDownwardIcon />}>
                            View Draw Size Chart
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* --- Draw Size Chart Card --- */}
            <Card id="draw-size-chart-card" className="myCard" sx={{ mb: 3, scrollMarginTop: scrollMarginTopValue }}>
                <CardHeader
                    // UPDATED TITLE
                    title={`Draw Size for ${displayDrawName} (${currentYear})`}
                    action={
                        <IconButton aria-label="download draw size chart" onClick={() => handleChartJsDownload(barChartRef, `Draw Size`, displayDrawName)}>
                            <DownloadIcon />
                        </IconButton>
                    }
                />
                <CardContent>
                    <Box sx={{ position: 'relative', height: '400px' }}>
                        <Bar
                            ref={barChartRef}
                            data={barChartData} // Uses updated barChartData with name in label
                            options={{
                                responsive: true, maintainAspectRatio: false,
                                plugins: {
                                    // watermarkPlugin, // Removed: Plugin is now registered globally
                                    tooltip: { mode: 'index', intersect: false },
                                    legend: { position: 'top' }
                                },
                                scales: { x: { display: true, title: { display: true, text: 'Draw Date' } }, y: { display: true, title: { display: true, text: 'Invitation Count' } } }
                            }}
                             // Alternatively, register plugin here if not global and not in options.plugins:
                            // plugins={[watermarkPlugin]}
                        />
                    </Box>
                    {distributionChartData && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pt: 1, borderTop: '1px solid var(--color-border)' }}>
                            <Button color="primary" variant="outlined" size="small" onClick={() => handleScrollTo('distribution-chart-card')} startIcon={<ArrowDownwardIcon />}>
                                View Distribution Chart
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* --- CRS Distribution Chart Card --- */}
            {distributionChartData && (
                <Card id="distribution-chart-card" className="myCard" sx={{ mb: 3, scrollMarginTop: scrollMarginTopValue }}>
                    <CardHeader
                        // UPDATED TITLE (using representative name, but keeping specific date)
                        title={`CRS Score Distribution for ${displayDrawName} Pool (as of ${distributionDate})`}
                        action={
                            <IconButton aria-label="download distribution chart" onClick={() => handleChartJsDownload(distributionChartRef, `CRS Distribution`, displayDrawName)}>
                                <DownloadIcon />
                            </IconButton>
                        }
                    />
                    <CardContent>
                        <Box sx={{ position: 'relative', height: '400px' }}>
                            <Bar
                                ref={distributionChartRef}
                                data={distributionChartData}
                                options={{
                                    responsive: true, maintainAspectRatio: false, indexAxis: 'x',
                                    plugins: {
                                        // watermarkPlugin, // Removed: Plugin is now registered globally
                                        tooltip: { mode: 'index', intersect: false },
                                        legend: { display: false }, // Legend less useful here
                                        // Update inner title too if desired
                                        title: { display: true, text: `Candidate Pool Distribution on ${distributionDate}` }
                                    },
                                    scales: { x: { display: true, title: { display: true, text: 'CRS Score Range' } }, y: { display: true, title: { display: true, text: 'Number of Candidates' } } }
                                }}
                                 // Alternatively, register plugin here if not global and not in options.plugins:
                                // plugins={[watermarkPlugin]}
                            />
                        </Box>
                        {latestDraw?.dd18 && (<Typography variant="caption" display="block" sx={{ mt: 2, textAlign: 'center' }}> Total Candidates in Pool: {latestDraw.dd18} </Typography>)}
                    </CardContent>
                </Card>
            )}

            {/* --- No Distribution Data Message --- */}
            {!distributionChartData && latestDraw && (
                <Card className="myCard" sx={{ mb: 3 }}>
                    <CardContent>
                        {/* Mention the specific draw name for which distribution is missing */}
                        <Typography variant="body2" color="textSecondary">
                            CRS Score Distribution data could not be displayed for the latest snapshot ({latestDraw.drawDistributionAsOn || 'date unavailable'}) associated with the {formatDrawName(latestDraw.drawName)} draw.
                        </Typography>
                    </CardContent>
                </Card>
            )}

        </Box>
    );
};

export default RecentInvitations;